import React, { useRef, useEffect, useState } from "react"
import { Chart, ArcElement, Tooltip } from "chart.js"
import type { GroupRow } from "~/api/snapshot_summaries"
import { formatCurrency } from "../utils"

Chart.register(ArcElement, Tooltip)

const COLORS = [
  "#143764",
  "#338cff",
  "#61bffd",
  "#4c9c8b",
  "#7cb9a5",
  "#c8873f",
  "#8a6fb0",
  "#c47b9a",
  "#9aa0a8",
]

interface SectorDonutProps {
  sectors: GroupRow[]
}

export default function SectorDonut({ sectors }: SectorDonutProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart<"doughnut"> | null>(null)
  const [hidden, setHidden] = useState<Set<number>>(new Set())

  const totalMv = sectors.reduce((s, r) => s + Number(r.performance.mv ?? 0), 0)

  const sorted = [...sectors]
    .filter((r) => Number(r.performance.mv ?? 0) > 0)
    .sort(
      (a, b) => Number(b.performance.mv ?? 0) - Number(a.performance.mv ?? 0),
    )

  const TOP = 8
  const topItems = sorted.slice(0, TOP)
  const rest = sorted.slice(TOP)
  const items =
    rest.length > 0
      ? [
          ...topItems,
          {
            label: `Other (${rest.length} sectors)`,
            performance: {
              mv: rest.reduce((s, r) => s + Number(r.performance.mv ?? 0), 0),
            } as GroupRow["performance"],
          } as GroupRow,
        ]
      : topItems

  useEffect(() => {
    if (!canvasRef.current || items.length === 0) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: items.map((r) => r.label),
        datasets: [
          {
            data: items.map((r) => Number(r.performance.mv ?? 0)),
            backgroundColor: items.map((_, i) => COLORS[i % COLORS.length]),
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      },
      options: {
        cutout: "58%",
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.raw as number
                const pct =
                  totalMv > 0 ? ((val / totalMv) * 100).toFixed(1) : "0.0"
                return `${ctx.label}: ${formatCurrency(val)} (${pct}%)`
              },
            },
          },
        },
      },
    })

    const onResize = () => chartRef.current?.resize()
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("resize", onResize)
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [items.length, totalMv])

  function toggleItem(index: number) {
    const chart = chartRef.current
    if (!chart) return
    chart.toggleDataVisibility(index)
    chart.update()
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  if (sectors.length === 0) {
    return <div className="b-ps-no-data">No sector data available.</div>
  }

  return (
    <div>
      <div className="b-ps-chart-wrap">
        <canvas ref={canvasRef} />
      </div>
      <div className="b-ps-legend">
        {items.map((item, i) => {
          const mv = Number(item.performance.mv ?? 0)
          const pct = totalMv > 0 ? ((mv / totalMv) * 100).toFixed(1) : "0.0"
          const isHidden = hidden.has(i)
          return (
            <div
              key={item.label}
              className="b-ps-legend-item"
              style={{ opacity: isHidden ? 0.4 : 1 }}
              onClick={() => toggleItem(i)}
            >
              <div
                className="b-ps-legend-swatch"
                style={{ background: COLORS[i % COLORS.length] }}
              />
              <span>
                {item.label} ({pct}%)
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
